/** Each service's engagement list (list or board view) and the engagement detail page. */
import { MODULE_LABELS, RETURN_TYPE_LABELS, type Module } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Columns3, List, Search } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { Empty, Loading, PageHeader, Pager, Section, Segmented, StatRow, useServerPaged } from "@/components/page";
import { ClosePanel } from "@/components/books";
import { EngagementTime, FeeField, money } from "@/components/time-tracker";
import { RequestsPanel } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { daysBetween, formatDate, formatDateTime, formatPeriod, initials, localToday } from "@/lib/format";
import { useMemberNames, useMembers } from "@/lib/members";
import type { Me } from "@/lib/session";
import { hasPermission } from "@/lib/session";
import type { ClientRequest, Engagement, Stage, Task, Template } from "@/lib/types";
import { SERVICE_PATHS } from "@/lib/types";
import { useSetNavHint } from "@/lib/nav-hint";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";

/** Due-date wording and tone for an open engagement. */
function dueState(dueDate: string | null, today: string): { label: string; tone: "danger" | "warning" | "muted" } {
  if (!dueDate) return { label: "No due date", tone: "muted" };
  const days = daysBetween(today, dueDate);
  if (days < 0) return { label: `${-days} day${days === -1 ? "" : "s"} past due`, tone: "danger" };
  if (days <= 7) return { label: days === 0 ? "Due today" : `Due in ${days} day${days === 1 ? "" : "s"}`, tone: "warning" };
  return { label: formatDate(dueDate), tone: "muted" };
}

const TONE = { danger: "text-destructive font-medium", warning: "text-warning font-medium", muted: "text-muted-foreground" } as const;

export function StageBadge({ name, last }: { name: string; last?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
        last ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-primary/10 text-primary",
      )}
    >
      {name}
    </span>
  );
}

function Assignee({ name }: { name: string | null }) {
  if (!name) return <span className="text-muted-foreground">Unassigned</span>;
  return (
    <span className="flex items-center gap-2">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-secondary-foreground">{initials(name)}</span>
      <span className="truncate">{name}</span>
    </span>
  );
}

/** Engagements shown in each board column; the rest of a busy stage is reached from the list. */
const BOARD_COLUMN = 50;

type EngagementPage = {
  items: Engagement[];
  total: number;
  counts: { all: number; pastDue: number; dueWeek: number; mine: number; byTemplate: Record<string, number> };
  stageCounts?: Record<string, number>;
};

export function WorkspacePage({ module, title, me, actions }: { module: Module; title: string; me: Me; actions?: ReactNode }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: templates, isLoading: tl } = useQuery<Template[]>({ queryKey: ["/api/templates"] });
  // Headline figures for all of the service's open work, whatever the list is filtered to.
  const { data: summary, isLoading: wl } = useQuery<EngagementPage>({ queryKey: ["/api/engagements?module=", module, "&limit=1"] });
  const names = useMemberNames();
  const members = useMembers();
  const today = localToday();
  const moduleTemplates = useMemo(() => (templates ?? []).filter((t) => t.module === module), [templates, module]);
  const [view, setView] = useState<"list" | "board">("list");
  const [templateId, setTemplateId] = useState("");
  const [stageKey, setStageKey] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [q, setQ] = useState("");
  const counts = summary?.counts;
  // The board needs one workflow's stages; default it to the workflow with the most open work.
  const busiest = useMemo(() => [...moduleTemplates].sort((x, y) => (counts?.byTemplate[y.id] ?? 0) - (counts?.byTemplate[x.id] ?? 0))[0], [moduleTemplates, counts]);
  const boardTemplate = moduleTemplates.find((t) => t.id === templateId) ?? busiest;
  const chosen = moduleTemplates.find((t) => t.id === templateId);
  const templateOf = (e: Engagement) => moduleTemplates.find((t) => t.id === e.templateId);
  // Work sitting in its workflow's last stage is finished in substance, so it isn't shown as late.
  const atLastStage = (e: Engagement) => {
    const st = templateOf(e)?.stages;
    return !!st?.length && st[st.length - 1]!.key === e.stageKey;
  };

  // The server filters and pages the list; the board gets the first few of each stage.
  const search = q.trim();
  const { offset, size, paged } = useServerPaged(`${templateId}|${stageKey}|${assignee}|${due}|${search}`);
  const params = new URLSearchParams();
  if (view === "board") {
    if (boardTemplate) params.set("templateId", boardTemplate.id);
    params.set("perStage", String(BOARD_COLUMN));
  } else {
    if (templateId) params.set("templateId", templateId);
    if (stageKey) params.set("stageKey", stageKey);
    params.set("offset", String(offset));
    params.set("limit", String(size));
  }
  if (assignee) params.set("assignee", assignee);
  if (due) params.set("due", due);
  if (search) params.set("q", search);
  const { data: page, isLoading: pl } = useQuery<EngagementPage>({
    queryKey: ["/api/engagements?module=", module, `&${params}`],
    enabled: view === "list" || !!boardTemplate,
    placeholderData: (prev) => prev,
  });
  const move = useMutation({
    mutationFn: (v: { id: string; stageKey: string }) => api.send("POST", `/api/engagements/${v.id}/move`, { stageKey: v.stageKey }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/engagements?module=", module] }),
    onError: (e) => toast({ title: "Stage not updated", description: errorMessage(e), variant: "destructive" }),
  });
  const canMove = hasPermission(me, "engagements.write");
  // Board columns come back for the chosen workflow only, so a stale page from another one is not shown.
  const filtered = (page?.items ?? []).filter((e) => view !== "board" || e.templateId === boardTemplate?.id);
  const pastDue = counts?.pastDue ?? 0;
  const dueWeek = counts?.dueWeek ?? 0;
  const mine = counts?.mine ?? 0;
  const stagesForFilter = (view === "board" ? boardTemplate : chosen)?.stages ?? [];

  if (tl || wl || (pl && !page)) return <Loading />;
  const crumbs = module === "payroll" ? undefined : [{ label: MODULE_LABELS[module], href: SERVICE_PATHS[module].home }];

  return (
    <>
      <PageHeader
        crumbs={crumbs}
        title={title}
        description={`Open engagements for ${MODULE_LABELS[module]}.`}
        actions={
          <>
          {actions}
          <Segmented
            label="View"
            value={view}
            onChange={(v) => {
              setView(v);
              setStageKey("");
            }}
            options={[
              { value: "list", label: <><List className="h-4 w-4" aria-hidden /> List</> },
              { value: "board", label: <><Columns3 className="h-4 w-4" aria-hidden /> Board</> },
            ]}
          />
          </>
        }
      />
      <StatRow
        stats={[
          { label: "Open engagements", value: counts?.all ?? 0 },
          { label: "Past due", value: pastDue, tone: pastDue ? "danger" : "default" },
          { label: "Due in the next 7 days", value: dueWeek, tone: dueWeek ? "warning" : "default" },
          { label: "Assigned to me", value: mine },
        ]}
      />
      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative lg:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input aria-label="Search engagements" placeholder="Search client or engagement" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2">
          {moduleTemplates.length > 1 && (
            <select
              aria-label="Workflow"
              className={select}
              value={view === "board" ? (boardTemplate?.id ?? "") : templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setStageKey("");
              }}
            >
              {view === "list" && <option value="">All workflows</option>}
              {moduleTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
          {view === "list" && (
            <select aria-label="Stage" className={select} value={stageKey} disabled={!stagesForFilter.length && moduleTemplates.length > 1} onChange={(e) => setStageKey(e.target.value)}>
              <option value="">{stagesForFilter.length || moduleTemplates.length <= 1 ? "All stages" : "All stages (select a workflow)"}</option>
              {(stagesForFilter.length ? stagesForFilter : (moduleTemplates[0]?.stages ?? [])).map((st) => (
                <option key={st.key} value={st.key}>
                  {st.name}
                </option>
              ))}
            </select>
          )}
          <select aria-label="Assigned to" className={select} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Everyone</option>
            <option value="me">Assigned to me</option>
            <option value="none">Unassigned</option>
            {members
              .filter((m) => m.isActive && m.role !== "client" && m.userId !== me.user?.id)
              .map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name}
                </option>
              ))}
          </select>
          <select aria-label="Due date" className={select} value={due} onChange={(e) => setDue(e.target.value)}>
            <option value="">Any due date</option>
            <option value="overdue">Past due</option>
            <option value="week">Due in the next 7 days</option>
          </select>
        </div>
      </div>

      {!moduleTemplates.length ? (
        <Empty>No workflow templates are configured for this service. Add one under Firm Settings › Workflows.</Empty>
      ) : view === "list" ? (
        !filtered.length ? (
          <Empty>{counts?.all ? "No engagements match these filters." : "There are no open engagements for this service."}</Empty>
        ) : (
          <TableScroll label="Open engagements table" className="rounded-lg border border-card-border bg-card">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Client</th>
                  <th className="px-4 py-2.5 font-medium">Engagement</th>
                  <th className="px-4 py-2.5 font-medium">Stage</th>
                  <th className="px-4 py-2.5 font-medium">Assigned to</th>
                  <th className="px-4 py-2.5 font-medium">Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((e) => {
                  const t = templateOf(e);
                  const idx = t?.stages.findIndex((st) => st.key === e.stageKey) ?? -1;
                  const d = idx >= 0 && t && idx === t.stages.length - 1 ? { label: "Ready to close", tone: "muted" as const } : dueState(e.dueDate, today);
                  return (
                    <tr key={e.id} className="hover:bg-muted/40">
                      <td className="px-4 py-3">
                        <Link href={`/engagements/${e.id}`} className="font-medium hover:underline">
                          {e.clientName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {t?.name ?? e.name}
                        {e.period && <span className="ml-1.5 text-foreground">· {formatPeriod(e.period)}</span>}
                      </td>
                      <td className="px-4 py-3">{t && idx >= 0 ? <StageBadge name={t.stages[idx]!.name} last={idx === t.stages.length - 1} /> : e.stageKey}</td>
                      <td className="px-4 py-3">
                        <Assignee name={e.assignedUserId ? (names.get(e.assignedUserId) ?? null) : null} />
                      </td>
                      <td className={cn("px-4 py-3 tabular", TONE[d.tone])}>{d.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager paged={paged(page?.total ?? 0)} noun="engagements" className="px-4 pb-3" />
          </TableScroll>
        )
      ) : (
        boardTemplate && (
          <div className="-mx-4 overflow-x-auto px-4 pb-4">
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${boardTemplate.stages.length}, minmax(220px, 1fr))` }}>
              {boardTemplate.stages.map((st, i) => {
                const col = filtered.filter((e) => e.stageKey === st.key);
                const inStage = page?.stageCounts?.[st.key] ?? col.length;
                const next = boardTemplate.stages[i + 1];
                return (
                  <div key={st.key} className="rounded-lg bg-muted/60 p-2">
                    <div className="mb-2 flex items-center justify-between px-2 pt-1">
                      <p className="text-xs font-semibold text-foreground/80">{st.name}</p>
                      <span className="rounded-full bg-background px-2 text-xs text-muted-foreground tabular">{inStage}</span>
                    </div>
                    <div className="space-y-2">
                      {col.map((e) => {
                        const d = atLastStage(e) ? { label: "Ready to close", tone: "muted" as const } : dueState(e.dueDate, today);
                        const who = e.assignedUserId ? names.get(e.assignedUserId) : undefined;
                        return (
                          <div key={e.id} className="rounded-md border border-card-border bg-card p-3">
                            <Link href={`/engagements/${e.id}`} className="block text-sm font-medium hover:underline">
                              {e.clientName}
                            </Link>
                            <p className="text-xs text-muted-foreground">{formatPeriod(e.period) || e.name}</p>
                            <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                              <span className={TONE[d.tone]}>{d.label}</span>
                              {who && (
                                <span title={who} className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[9px] font-semibold">
                                  {initials(who)}
                                </span>
                              )}
                            </div>
                            {canMove && next && (
                              <button
                                onClick={() => move.mutate({ id: e.id, stageKey: next.key })}
                                title={`Move to ${next.name}`}
                                className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-border py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                              >
                                Advance to next stage <ArrowRight className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {inStage > col.length && (
                      <button
                        type="button"
                        onClick={() => {
                          setTemplateId(boardTemplate.id);
                          setStageKey(st.key);
                          setView("list");
                        }}
                        className="mt-2 w-full rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-background hover:text-foreground"
                      >
                        {(inStage - col.length).toLocaleString("en-US")} more in this stage. Switch to the list to see them all.
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}
    </>
  );
}

type EngagementDetail = {
  engagement: Engagement;
  stages: Stage[];
  events: Array<{ id: string; type: string; fromStage: string | null; toStage: string | null; createdAt: string }>;
  tasks: Task[];
  requests: ClientRequest[];
  dueDates: { original: string; extended: string } | null;
};

const EVENT_LABELS: Record<string, string> = { created: "Engagement created", completed: "Marked complete", reopened: "Reopened", cancelled: "Cancelled" };

/** Assigned staff member and due date, editable by staff who can manage work. */
function Assignment({ e, canEdit, onSaved, me }: { e: Engagement; canEdit: boolean; onSaved: () => void; me: Me }) {
  const members = useMembers();
  const names = useMemberNames();
  const who = e.module === "tax" ? "Preparer" : "Assigned to";
  const save = useMutation({
    mutationFn: (patch: { assignedUserId?: string | null; dueDate?: string | null }) => api.send("PATCH", `/api/engagements/${e.id}`, patch),
    onSuccess: onSaved,
  });
  const seesFee = hasPermission(me, "billing.read");
  const fee = (
    <FeeField
      id={`fee-${e.id}`}
      label="Quoted fee"
      cents={e.quotedFeeCents}
      disabled={!canEdit || !hasPermission(me, "billing.write")}
      onSave={(quotedFeeCents) => api.send("PATCH", `/api/engagements/${e.id}`, { quotedFeeCents }).then(onSaved)}
    />
  );
  if (!canEdit) {
    return (
      <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted-foreground">{who}</dt>
          <dd>{e.assignedUserId ? (names.get(e.assignedUserId) ?? "—") : "Unassigned"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Due date</dt>
          <dd>{formatDate(e.dueDate)}</dd>
        </div>
        {seesFee && (
          <div>
            <dt className="text-xs text-muted-foreground">Quoted fee</dt>
            <dd className="tabular">{money(e.quotedFeeCents, true)}</dd>
          </div>
        )}
      </dl>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <label className="space-y-1 text-sm">
        <span className="text-xs text-muted-foreground">{who}</span>
        <select
          className={cn(select, "w-full")}
          value={e.assignedUserId ?? ""}
          disabled={save.isPending}
          onChange={(ev) => save.mutate({ assignedUserId: ev.target.value || null })}
        >
          <option value="">Unassigned</option>
          {members
            .filter((m) => (m.isActive && m.role !== "client" && m.role !== "readonly") || m.userId === e.assignedUserId)
            .map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
        </select>
      </label>
      <label className="space-y-1 text-sm">
        <span className="text-xs text-muted-foreground">Due date</span>
        <Input type="date" value={e.dueDate ?? ""} disabled={save.isPending} onChange={(ev) => save.mutate({ dueDate: ev.target.value || null })} />
      </label>
      {seesFee && fee}
    </div>
  );
}

/**
 * The preparer and reviewer on a tax return, its due dates, and the reviewer's sign-off. The
 * return cannot move past Review, or go to the client for signature, until the reviewer signs off.
 */
function ReturnReview({ e, stages, dueDates, me, onSaved }: { e: Engagement; stages: Stage[]; dueDates: EngagementDetail["dueDates"]; me: Me; onSaved: () => void }) {
  const members = useMembers();
  const names = useMemberNames();
  const canEdit = hasPermission(me, "engagements.write") && e.status === "open";
  const setReviewer = useMutation({ mutationFn: (reviewerUserId: string | null) => api.send("PATCH", `/api/engagements/${e.id}`, { reviewerUserId }), onSuccess: onSaved });
  const signOff = useMutation({ mutationFn: () => api.send("POST", `/api/engagements/${e.id}/review-signoff`), onSuccess: onSaved });
  const setForm = useMutation({ meta: { inlineError: true }, mutationFn: (returnType: string) => api.send("PATCH", `/api/engagements/${e.id}`, { returnType }), onSuccess: onSaved });
  const hasReview = stages.some((s) => s.key === "review");
  const atReview = e.stageKey === "review";
  const meId = me.user?.id;
  const ownerOrAdmin = me.role === "owner" || me.role === "admin";
  const maySign = canEdit && atReview && !e.reviewSignedOffAt && meId !== e.assignedUserId && (!e.reviewerUserId || e.reviewerUserId === meId || ownerOrAdmin);
  const label = e.returnType ? RETURN_TYPE_LABELS[e.returnType] : null;
  return (
    <div className="mt-4 grid gap-4 border-t border-border pt-4 text-sm sm:grid-cols-3">
      <div>
        <p className="text-xs text-muted-foreground">Return</p>
        {label || !canEdit ? (
          <p className="font-medium">{label ?? "Tax return"}</p>
        ) : (
          // Work started from a workflow that doesn't name the form: choose it once.
          <select aria-label="Return form" className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-sm" value="" onChange={(ev) => ev.target.value && setForm.mutate(ev.target.value)} disabled={setForm.isPending}>
            <option value="">Choose the form…</option>
            {(Object.entries(RETURN_TYPE_LABELS) as Array<[string, string]>).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        )}
        {setForm.error && <p className="text-xs text-destructive">{errorMessage(setForm.error)}</p>}
        {e.yearEndMonth && <p className="text-xs text-muted-foreground">Fiscal year ends {new Date(Date.UTC(2000, e.yearEndMonth - 1, 1)).toLocaleString("en-US", { month: "long", timeZone: "UTC" })}</p>}
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Federal due dates</p>
        {dueDates ? (
          <p className="tabular">
            {formatDate(dueDates.original)} <span className="text-muted-foreground">· extended {formatDate(dueDates.extended)}</span>
          </p>
        ) : (
          <p className="text-muted-foreground">Set the tax year to calculate</p>
        )}
      </div>
      <label className="space-y-1">
        <span className="text-xs text-muted-foreground">Reviewer</span>
        {canEdit && !e.reviewSignedOffAt ? (
          <select className={cn(select, "w-full")} value={e.reviewerUserId ?? ""} disabled={setReviewer.isPending} onChange={(ev) => setReviewer.mutate(ev.target.value || null)}>
            <option value="">Not assigned</option>
            {members
              .filter((m) => (m.isActive && m.role !== "client" && m.role !== "readonly" && m.userId !== e.assignedUserId) || m.userId === e.reviewerUserId)
              .map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name}
                </option>
              ))}
          </select>
        ) : (
          <span className="block">{e.reviewerUserId ? (names.get(e.reviewerUserId) ?? "—") : "Not assigned"}</span>
        )}
      </label>
      {hasReview && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 sm:col-span-3",
            e.reviewSignedOffAt ? "border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40" : atReview ? "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40" : "border-border bg-muted/30",
          )}
        >
          <span>
            {e.reviewSignedOffAt
              ? `Reviewed and signed off by ${names.get(e.reviewSignedOffByUserId ?? "") ?? "the reviewer"} on ${formatDateTime(e.reviewSignedOffAt)}.`
              : atReview
                ? "Waiting for reviewer sign-off. The return cannot go to the client for signature until it is signed off."
                : "Reviewer sign-off is required at the Review stage, before the client signs."}
          </span>
          {maySign && (
            <Button size="sm" onClick={() => signOff.mutate()} disabled={signOff.isPending}>
              <Check className="h-4 w-4" /> Sign off review
            </Button>
          )}
        </div>
      )}
      {signOff.error && <p className="text-sm text-destructive sm:col-span-3">{errorMessage(signOff.error)}</p>}
    </div>
  );
}

export function EngagementPage({ id, me }: { id: string; me: Me }) {
  const qc = useQueryClient();
  const key = ["/api/engagements/", id];
  const { data, isLoading, error } = useQuery<EngagementDetail>({ queryKey: key });
  const { data: client, isLoading: clientLoading } = useQuery<{ client: { displayName: string } }>({ queryKey: ["/api/clients/", data?.engagement.clientId ?? ""], enabled: !!data });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const move = useMutation({ mutationFn: (stageKey: string) => api.send("POST", `/api/engagements/${id}/move`, { stageKey }), onSuccess: refresh });
  const setStatus = useMutation({ mutationFn: (action: "complete" | "reopen") => api.send("POST", `/api/engagements/${id}/${action}`), onSuccess: refresh });
  const toggleTask = useMutation({
    mutationFn: (t: Task) => api.send("PATCH", `/api/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" }),
    onSuccess: refresh,
  });
  useSetNavHint(data ? SERVICE_PATHS[data.engagement.module].home : null);
  const { data: time, isLoading: timeLoading } = useQuery<{ enabled: boolean; entries: unknown[] }>({ queryKey: [`/api/engagements/${id}/time`], enabled: !!data });
  // The Time section sits above Tasks, and the client's name sits in the header and breadcrumbs (where a
  // long one wraps); wait for both so neither can push the page down after first paint.
  if (isLoading || timeLoading || clientLoading) return <Loading tall />;
  if (error || !data) return <Empty>Engagement not found.</Empty>;
  const e = data.engagement;
  const idx = data.stages.findIndex((s) => s.key === e.stageKey);
  const canEdit = hasPermission(me, "engagements.write");
  // A close is finished from its checklist; only an owner or admin records one closed elsewhere.
  const canComplete = canEdit && !(e.module === "bookkeeping" && e.period && me.role !== "owner" && me.role !== "admin");
  const stageName = (k: string | null) => data.stages.find((s) => s.key === k)?.name ?? k;
  const paths = SERVICE_PATHS[e.module];

  return (
    <>
      <PageHeader
        crumbs={[
          { label: MODULE_LABELS[e.module], href: paths.home },
          ...(paths.workflow !== paths.home ? [{ label: paths.workflowLabel, href: paths.workflow }] : []),
          { label: client?.client.displayName ?? "Client", href: `/clients/${e.clientId}` },
        ]}
        back={e.kind === "planning" ? { label: "Tax Planning", href: "/tax/planning" } : { label: paths.workflowLabel, href: paths.workflow }}
        title={e.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Link href={`/clients/${e.clientId}`} className="font-medium text-foreground hover:underline">
              {client?.client.displayName ?? "View client record"}
            </Link>
            <span aria-hidden>·</span>
            <span>{e.status === "open" ? "Open" : e.status === "completed" ? "Complete" : "Cancelled"}</span>
          </span>
        }
        actions={
          canEdit &&
          (e.status === "open" ? (
            canComplete && (
              <Button variant="outline" onClick={() => setStatus.mutate("complete")}>
                <Check className="h-4 w-4" /> Mark complete
              </Button>
            )
          ) : (
            <Button variant="outline" onClick={() => setStatus.mutate("reopen")}>
              Reopen
            </Button>
          ))
        }
      />

      <div className="mb-6 rounded-lg border border-card-border bg-card p-5">
        <ol className="flex flex-wrap gap-1.5" aria-label="Stages">
          {data.stages.map((s, i) => (
            <li key={s.key}>
              <button
                disabled={!canEdit || e.status !== "open"}
                onClick={() => move.mutate(s.key)}
                aria-current={i === idx ? "step" : undefined}
                className={cn(
                  "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition",
                  i === idx ? "border-primary bg-primary text-primary-foreground" : i < idx ? "border-primary/20 bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-muted",
                )}
                title={`Client portal status: "${s.clientLabel}"`}
              >
                {i < idx && <Check className="h-3 w-3" />}
                {s.name}
              </button>
            </li>
          ))}
        </ol>
        {idx >= 0 && (
          <p className="mt-3 text-sm text-muted-foreground">
            Client portal status: <span className="font-medium text-foreground">{data.stages[idx]!.clientLabel}</span>
          </p>
        )}
        {e.status === "open" && idx === data.stages.length - 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm dark:border-emerald-900 dark:bg-emerald-950/40">
            <span>This engagement has reached its final stage. Mark it complete to close it and remove it from open work.</span>
            {canComplete && (
              <Button size="sm" onClick={() => setStatus.mutate("complete")} disabled={setStatus.isPending}>
                <Check className="h-4 w-4" /> Mark complete
              </Button>
            )}
          </div>
        )}
        <div className="mt-4 border-t border-border pt-4">
          <Assignment e={e} canEdit={canEdit} onSaved={refresh} me={me} />
          {e.module === "tax" && <ReturnReview e={e} stages={data.stages} dueDates={data.dueDates} me={me} onSaved={refresh} />}
        </div>
      </div>

      {time?.enabled || (time?.entries.length ?? 0) > 0 ? (
        <Section title="Time" className="mb-6" description="Time logged on this engagement. Firm Analytics uses it for workload and profitability.">
          <EngagementTime engagementId={e.id} me={me} open={e.status === "open"} />
        </Section>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Tasks">
          {data.tasks.length === 0 ? (
            <Empty>No tasks for this engagement.</Empty>
          ) : (
            <ul className="space-y-1">
              {data.tasks.map((t) => (
                <li key={t.id}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
                    <input type="checkbox" checked={t.status === "done"} onChange={() => toggleTask.mutate(t)} disabled={!hasPermission(me, "tasks.write")} />
                    <span className={`flex-1 text-sm ${t.status === "done" ? "text-muted-foreground line-through" : ""}`}>{t.title}</span>
                    {t.dueDate && <span className="text-xs text-muted-foreground tabular">{formatDate(t.dueDate)}</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title="Activity">
          <ul className="space-y-2 text-sm">
            {data.events.map((ev) => (
              <li key={ev.id} className="flex justify-between gap-2">
                <span>
                  {ev.type === "stage_changed" || ev.type === "auto_advanced" ? (
                    <>
                      {stageName(ev.fromStage)} → <span className="font-medium">{stageName(ev.toStage)}</span>
                      {ev.type === "auto_advanced" && <span className="text-muted-foreground"> (automatic)</span>}
                    </>
                  ) : (
                    (EVENT_LABELS[ev.type] ?? ev.type.replace(/_/g, " "))
                  )}
                </span>
                <span className="whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(ev.createdAt)}</span>
              </li>
            ))}
          </ul>
        </Section>
        {e.module === "bookkeeping" && e.period && (
          <Section title="Month-end close checklist" className="lg:col-span-2">
            <ClosePanel engagementId={e.id} clientId={e.clientId} me={me} />
          </Section>
        )}
        <Section title="Client requests" className="lg:col-span-2">
          <RequestsPanel clientId={e.clientId} engagementId={e.id} canEdit={hasPermission(me, "requests.write")} />
        </Section>
      </div>
    </>
  );
}
